// Design tokens — Respostas rápidas.
// Referência: .md/DESIGN_UIUX.md (Painel de Configurações) e templates-whatsapp.
//
// Linguagem visual:
// - Neutros zinc (#18181b → #a1a1aa) sobre fundo #f4f4f7.
// - Roxo (#6d28d9) SÓ como acento de interface: ação primária, seleção, link.
//   Nunca classifica conteúdo.
// - O tipo da resposta é sinalizado por tile tonal: texto = zinc, arquivo = âmbar,
//   áudio = verde (o verde da mensagem de voz do WhatsApp).
// - Preview WhatsApp usa cores de produto (#d9fdd3, #008069, #efeae2) —
//   exceção documentada, restrita ao mockup.

export const RESPOSTAS_RAPIDAS_DESIGN = {
  // ---------- shell ----------
  root: 'flex flex-col min-h-full w-full bg-[#f4f4f7] text-[#18181b] animate-in fade-in slide-in-from-bottom-4 duration-500',
  fontStack: "'Plus Jakarta Sans', Manrope, ui-sans-serif, system-ui, sans-serif",
  container: 'w-full px-4 md:px-6 lg:px-8',
  pageBody: 'pb-[120px]',

  // ---------- barra de topo ----------
  stickyHeader: 'sticky top-0 z-30 bg-white border-b border-[#e7e7ec]',
  topbar: 'flex flex-col md:flex-row md:items-start gap-4 pt-[18px] pb-[18px]',
  topbarIdentity: 'flex items-start gap-3.5 min-w-0 flex-1',
  iconTile:
    'flex items-center justify-center flex-shrink-0 w-[42px] h-[42px] rounded-[10px] bg-[#ede9fe] text-[#6d28d9]',
  title: 'text-[20px] font-bold tracking-[-0.02em] leading-tight text-[#18181b]',
  subtitle: 'mt-[5px] text-[13px] text-[#71717a] leading-[1.5]',
  topbarActions: 'flex items-center gap-2.5 flex-shrink-0 flex-wrap md:pt-[5px]',

  // ---------- cabeçalho de seção ----------
  sectionHeader: 'mt-[18px] first:mt-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3',
  sectionHeaderMain: 'min-w-0',
  sectionHeaderTitle: 'text-[13px] font-bold uppercase tracking-[0.06em] text-[#a1a1aa]',
  sectionHeaderSub: 'mt-1.5 text-[13px] text-[#71717a] leading-[1.5]',

  // ---------- cartões ----------
  stack: 'flex flex-col gap-3.5',
  card: 'bg-white border border-[#e7e7ec] rounded-[12px] p-[26px] overflow-hidden min-w-0',
  cardFlush: 'bg-white border border-[#e7e7ec] rounded-[12px] overflow-hidden min-w-0',
  divider: 'h-px bg-[#f0f0f3]',

  // ---------- filtro por tipo ----------
  // Chip compacto, largura do conteúdo. Filtrar é um controle, não um painel
  // de métricas — a contagem cabe dentro do próprio chip.
  controlBar: 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3',
  filterBar: 'flex flex-wrap items-center gap-2',
  filterChip:
    'inline-flex items-center gap-2 px-3 py-[7px] rounded-lg border text-[13px] font-semibold whitespace-nowrap cursor-pointer transition-colors',
  filterChipOff: 'bg-white border-[#e7e7ec] text-[#52525b] hover:border-[#d4d4d8] hover:text-[#18181b]',
  filterChipOn: 'bg-[#ede9fe] border-[#a78bfa] text-[#5b21b6]',
  filterCount: 'text-[12px] font-semibold tabular-nums rounded px-1.5 py-px bg-[#f4f4f5] text-[#71717a]',
  filterCountOn: 'text-[12px] font-semibold tabular-nums rounded px-1.5 py-px bg-white/70 text-[#5b21b6]',

  // tiles tonais por tipo de conteúdo
  typeTexto: 'bg-[#f4f4f5] text-[#52525b]',
  typeArquivo: 'bg-[#fffaeb] text-[#b54708]',
  typeAudio: 'bg-[#ecfdf3] text-[#067647]',

  // ---------- campos ----------
  field: 'min-w-0',
  label: 'block text-[13px] font-semibold text-[#18181b] mb-[7px]',
  hint: 'mt-[7px] text-[12px] text-[#a1a1aa] leading-[1.5]',
  input:
    'w-full box-border h-auto px-3.5 py-[11px] rounded-lg border border-[#d4d4d8] bg-white text-[14px] md:text-[14px] text-[#18181b] shadow-none transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',
  inputSearch:
    'w-full box-border h-auto pl-9 pr-3.5 py-[9px] rounded-lg border border-[#d4d4d8] bg-white text-[14px] md:text-[14px] text-[#18181b] shadow-none transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',
  searchWrap: 'relative w-full sm:w-[280px]',
  searchIcon: 'absolute left-3 top-1/2 -translate-y-1/2 text-[#a1a1aa] pointer-events-none',
  textarea:
    'w-full box-border px-4 py-3.5 rounded-lg border border-[#d4d4d8] bg-white text-[14px] leading-[1.6] text-[#18181b] shadow-none resize-y transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',

  // ---------- botões ----------
  primaryButton:
    'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-[10px] bg-gradient-to-b from-[#8b5cf6] to-[#7c3aed] text-white text-[14px] font-semibold whitespace-nowrap shadow-[0_2px_8px_rgba(124,58,237,.32)] transition-colors hover:from-[#7c3aed] hover:to-[#6d28d9] disabled:from-[#e4e4e7] disabled:to-[#e4e4e7] disabled:text-[#a1a1aa] disabled:shadow-none disabled:cursor-not-allowed',
  ghostButton:
    'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#52525b] whitespace-nowrap transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b] disabled:opacity-50 disabled:cursor-not-allowed',
  dangerButton:
    'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#b42318] whitespace-nowrap transition-colors hover:bg-[#fef3f2] hover:border-[#fecdca] disabled:opacity-50 disabled:cursor-not-allowed',
  iconButton:
    'inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[#e7e7ec] bg-white text-[#71717a] transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b]',
  iconButtonDanger:
    'inline-flex items-center justify-center w-8 h-8 rounded-lg border border-[#e7e7ec] bg-white text-[#b42318] transition-colors hover:bg-[#fef3f2] hover:border-[#fecdca]',
  link: 'text-[13px] font-semibold text-[#6d28d9] transition-colors hover:text-[#5b21b6] hover:underline',

  // ---------- seletor de tipo (drawer) ----------
  choiceGrid: 'grid grid-cols-3 gap-2.5',
  choiceCard:
    'px-3 py-3 rounded-lg border transition-colors cursor-pointer flex flex-col items-center justify-center gap-1.5',
  choiceCardOn: 'bg-[#faf8ff] border-[#a78bfa]',
  choiceCardOff: 'bg-white border-[#e7e7ec] hover:border-[#d4d4d8]',
  choiceTitleOn: 'text-[13px] font-semibold text-[#5b21b6]',
  choiceTitleOff: 'text-[13px] font-semibold text-[#52525b]',
  choiceLocked: 'flex items-center gap-2.5 px-3.5 py-3 rounded-lg border border-[#e7e7ec] bg-[#fafafb]',

  // ---------- badges ----------
  badge:
    'inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-md text-[12px] font-semibold whitespace-nowrap flex-shrink-0 border',
  badgeNeutral: 'bg-[#f4f4f5] text-[#71717a] border-[#e7e7ec]',
  badgeWarn: 'bg-[#fffaeb] text-[#b54708] border-[#fedf89]',
  badgeOk: 'bg-[#ecfdf3] text-[#067647] border-[#abefc6]',

  // ---------- tabela ----------
  tableWrap: 'w-full overflow-x-auto min-w-0',
  tableHeadRow: 'flex items-center gap-4 px-[26px] py-3 border-b border-[#f0f0f3] bg-[#fafafb]',
  tableHead: 'text-[12px] font-semibold uppercase tracking-[0.04em] text-[#a1a1aa]',
  tableRow:
    'group flex items-center gap-4 px-[26px] py-3.5 border-b border-[#f0f0f3] last:border-b-0 transition-colors hover:bg-[#fafafb]',
  tableIcon: 'flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0',
  tableName: 'text-[14px] font-semibold text-[#18181b] truncate',
  tableSnippet: 'mt-0.5 text-[13px] text-[#71717a] truncate',
  tableDate: 'text-[13px] text-[#a1a1aa] tabular-nums whitespace-nowrap',
  tableActions: 'flex justify-end gap-1.5 flex-shrink-0',

  // ---------- empty ----------
  emptyWrap: 'px-2 py-12 text-center',
  emptyIcon:
    'mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-[12px] bg-[#f4f4f5] text-[#a1a1aa]',
  emptyTitle: 'text-[15px] font-semibold text-[#18181b]',
  emptyDesc: 'mt-1.5 text-[13px] text-[#71717a] leading-[1.5] max-w-sm mx-auto',
  noticeInfo:
    'flex items-start gap-2.5 px-4 py-3.5 rounded-[10px] border border-[#e7e7ec] bg-[#fafafb] text-[13px] text-[#71717a] leading-[1.5]',
  loadingBar: 'h-1 bg-[#f4f4f5] rounded-full overflow-hidden',
  loadingBarInner: 'h-full bg-[#6d28d9] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]',

  // ---------- drawer ----------
  drawerOverlay: 'fixed inset-0 z-[9999] flex justify-end overflow-hidden animate-in fade-in duration-200',
  // Sem backdrop-blur: filtro em tela cheia recompõe o dashboard inteiro a cada
  // frame enquanto o painel desliza — era o que travava a abertura.
  drawerBackdrop: 'absolute inset-0 bg-black/40',
  drawerPanel:
    'relative w-full max-w-[1040px] h-full bg-white border-l border-[#e7e7ec] flex flex-col animate-in slide-in-from-right duration-300',
  drawerHeader:
    'flex items-center justify-between gap-4 px-[26px] py-[18px] border-b border-[#e7e7ec] bg-white',
  drawerTitle: 'text-[15px] font-semibold text-[#18181b]',
  drawerSubtitle: 'mt-0.5 text-[13px] text-[#71717a]',
  drawerClose:
    'flex items-center justify-center w-8 h-8 rounded-lg text-[#71717a] transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b]',
  drawerBody: 'flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0',
  drawerLeft: 'w-full lg:w-[58%] p-[26px] overflow-y-auto lg:border-r border-[#f0f0f3] bg-white',
  drawerRight: 'hidden lg:block w-[42%] p-[26px] overflow-y-auto bg-[#fafafb]',
  drawerFooter:
    'border-t border-[#e7e7ec] bg-[#fafafb] px-[26px] py-4 flex items-center justify-between gap-3',
  drawerFieldStack: 'flex flex-col gap-6',

  // ---------- dropzone (arquivo) ----------
  dropzone:
    'flex flex-col items-center justify-center gap-2 px-6 py-9 rounded-[10px] border border-dashed border-[#d4d4d8] bg-[#fafafb] text-center cursor-pointer transition-colors hover:border-[#a78bfa] hover:bg-[#faf8ff]',
  dropzoneActive: 'border-[#8b5cf6] bg-[#f5f1ff]',
  dropzoneIcon: 'flex items-center justify-center w-11 h-11 rounded-[10px] bg-[#f4f4f5] text-[#a1a1aa]',
  dropzoneTitle: 'text-[13px] font-semibold text-[#18181b]',
  dropzoneHint: 'text-[12px] text-[#a1a1aa]',
  fileRow: 'flex items-center gap-3 px-3.5 py-3 rounded-[10px] border border-[#e7e7ec] bg-white min-w-0',
  fileIcon: 'flex items-center justify-center w-9 h-9 rounded-lg bg-[#fffaeb] text-[#b54708] flex-shrink-0',
  fileName: 'text-[13px] font-semibold text-[#18181b] truncate',
  fileMeta: 'mt-0.5 text-[12px] text-[#a1a1aa]',

  // ---------- gravador (áudio) ----------
  recorderWrap:
    'flex flex-col items-center justify-center gap-3 px-6 py-9 rounded-[10px] border border-dashed border-[#d4d4d8] bg-[#fafafb] text-center',
  recorderButton:
    'flex items-center justify-center w-14 h-14 rounded-full bg-[#ecfdf3] text-[#067647] border border-[#abefc6] transition-colors cursor-pointer hover:bg-[#d1fadf]',
  recorderTimer: 'text-[24px] font-bold tabular-nums leading-none text-[#18181b]',
  recorderTimerWarn: 'text-[24px] font-bold tabular-nums leading-none text-[#b54708]',
  recorderStatus: 'flex items-center gap-2 text-[13px] text-[#71717a]',
  recorderDot: 'w-2 h-2 rounded-full bg-[#f04438] animate-pulse',
  recorderStop:
    'flex items-center justify-center w-11 h-11 rounded-full bg-[#fef3f2] text-[#b42318] border border-[#fecdca] transition-colors cursor-pointer hover:bg-[#fee4e2]',
  recorderKeep:
    'flex items-center justify-center w-11 h-11 rounded-full bg-[#ecfdf3] text-[#067647] border border-[#abefc6] transition-colors cursor-pointer hover:bg-[#d1fadf]',

  // ---------- preview WhatsApp (cores de produto) ----------
  previewPhoneFrame: 'bg-[#18181b] rounded-[2.5rem] p-1.5 mx-auto max-w-[300px]',
  previewPhoneInner: 'bg-[#18181b] rounded-[2rem] overflow-hidden',
  previewNotch: 'flex justify-center py-2 bg-[#18181b]',
  previewNotchPill: 'w-20 h-5 bg-[#27272a] rounded-full',
  previewWhatsappHeader: 'bg-[#008069] px-3 py-2.5 flex items-center gap-2.5',
  previewChatArea: 'min-h-[300px] p-3 flex flex-col justify-end',
  previewMessageBubble:
    'bg-[#d9fdd3] rounded-xl rounded-tr-sm p-2.5 shadow-[0_1px_2px_rgba(24,24,27,.08)] max-w-[88%] min-w-[150px] relative ml-auto',
  previewMessageTail:
    'absolute -right-1 top-0 w-0 h-0 border-l-[8px] border-l-[#d9fdd3] border-t-[8px] border-t-transparent',
  previewText: 'text-[12.5px] leading-[1.5] text-[#111b21] whitespace-pre-wrap break-words',
  previewPlaceholder: 'text-[12.5px] leading-[1.5] text-[#667781] italic',
  previewTimestamp: 'flex items-center justify-end gap-1 mt-1 text-[10px] text-[#667781]',
  previewInputBar: 'bg-[#f0f0f0] px-2 py-2 flex items-center gap-2',
  previewHomeIndicator: 'flex justify-center py-2 bg-[#18181b]',
  previewDocCard: 'flex items-center gap-2.5 bg-[#cdf3c4] rounded-lg px-2.5 py-2 mb-1 min-w-0',
  previewVoiceRow: 'flex items-center gap-2.5',
  previewVoiceTrack: 'flex-1 h-[3px] rounded-full bg-[#8fbf87] relative',
  previewVoiceKnob: 'absolute left-0 -top-[3.5px] w-2.5 h-2.5 rounded-full bg-[#008069]',
  previewCaption: 'mt-3 text-[12px] text-[#a1a1aa] text-center leading-[1.5]',
};

const D = RESPOSTAS_RAPIDAS_DESIGN;

// aliases legados (consumidores que ainda usam a árvore antiga)
D.page = {
  container: D.root,
  header: { container: D.topbar, title: D.title, subtitle: D.subtitle, actionButton: D.primaryButton },
};
D.dialog = {
  section: { label: D.label, input: D.input },
  actions: {
    cancelButton: D.ghostButton,
    confirmButton: { base: D.primaryButton, active: '', disabled: 'opacity-50 cursor-not-allowed shadow-none' },
  },
};

export default RESPOSTAS_RAPIDAS_DESIGN;
